import React, { createContext, useEffect, useState } from 'react'

import PropTypes from 'prop-types'

import { BrowserRouter as Router, Route, Routes, useLocation, useNavigate } from 'react-router-dom'

import { ThemeProvider } from '@mui/material/styles'
import { CircularProgress } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'

import locale from 'date-fns/locale/en-US'

import LoginPage from './pages/LoginPage'
import { getToken } from './useToken'

import SystemMessage from './components/SystemMessage'
import TablePage from './pages/TablePage'
import Navigation from './components/Navigation'
import UsersPage from './pages/UsersPage'
import ProfilePage from './pages/ProfilePage'
import PrivacyPolicyPage from './pages/PrivacyPolicyPage'
import StatisticsPage from './pages/StatisticsPage'
import StatisticsEditPage from './pages/StatisticsEditPage'
import StatisticsViewPage from './pages/StatisticsViewPage'
import ScanPage from './pages/ScanPage'
import Footer from './components/Footer'

import theme from './theme'

export const AppSettingsContext = createContext()

if (locale && locale.options) {
  locale.options.weekStartsOn = 1
}

function App() {
  const [selectedRows, setSelectedRows] = useState(new Set())
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [isBusy, setIsBusy] = useState(true)
  const token = getToken()

  useEffect(() => {
    setIsLoggedIn(token != null)
    setIsBusy(false)
  }, [])

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} locale={locale}>
      <ThemeProvider theme={theme}>
        <Router>
          <AppSettingsContext.Provider
            value={{
              isLoggedIn: isLoggedIn,
              setIsLoggedIn: setIsLoggedIn,
              selectedRows: selectedRows,
              setSelectedRows: setSelectedRows,
            }}
          >
            {isBusy ? <CircularProgress /> : <AppContent isLoggedIn={isLoggedIn} />}
            <SystemMessage />
          </AppSettingsContext.Provider>
        </Router>
      </ThemeProvider>
    </LocalizationProvider>
  )
}

function AppContent({ isLoggedIn }) {
  const locate = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    const isPublicRoute = locate.pathname === '/login' || locate.pathname === '/privacy'

    if (!isLoggedIn && !isPublicRoute) {
      navigate('/login')
    } else if (isLoggedIn && locate.pathname === '/') {
      navigate('/login')
    }
  }, [isLoggedIn, locate.pathname, navigate])

  return (
    <Routes>
      <Route
        path="/login"
        element={
          <>
            <Navigation />
            <LoginPage />
            <Footer />
          </>
        }
      />
      <Route
        path="/statistics/edit/:id"
        element={
          <>
            <Navigation />
            <StatisticsEditPage />
            <Footer />
          </>
        }
      />
      <Route
        path="/statistics/edit/"
        element={
          <>
            <Navigation />
            <StatisticsEditPage />
            <Footer />
          </>
        }
      />
      <Route path="/statistics/view/:id" element={<StatisticsViewPage />} />
      <Route
        path="/statistics"
        element={
          <>
            <Navigation />
            <StatisticsPage />
            <Footer />
          </>
        }
      />
      <Route
        path="/scan"
        element={
          <>
            <Navigation />
            <ScanPage />
            <Footer />
          </>
        }
      />
      <Route
        path="/table"
        element={
          <>
            <Navigation />
            <TablePage />
            <Footer />
          </>
        }
      />
      <Route
        path="/users"
        element={
          <>
            <Navigation />
            <UsersPage />
            <Footer />
          </>
        }
      />
      <Route
        path="/profile"
        element={
          <>
            <Navigation />
            <ProfilePage />
            <Footer />
          </>
        }
      />
      <Route
        path="/privacy"
        element={
          <>
            <Navigation />
            <PrivacyPolicyPage />
            <Footer />
          </>
        }
      />
    </Routes>
  )
}

AppContent.propTypes = {
  isLoggedIn: PropTypes.bool.isRequired,
}

export default App
